/*
 * Product image zoom — world-class mobile experience via PhotoSwipe v5.
 *
 * Why this exists: Dawn's built-in zoom is desktop-only. "Click and hover"
 * (magnify.js) is mouse-driven (onmousemove/onmouseleave) and does nothing
 * useful on touch; the "Open lightbox" modal is just a fit-to-width scroll
 * container with no pinch-zoom or pan. On mobile both leave customers unable
 * to inspect detail/alignment in our photography.
 *
 * This script intercepts taps on the main product image (any image_zoom mode)
 * on touch / small-screen devices and opens a PhotoSwipe lightbox with true
 * pinch-to-zoom, drag-to-pan, double-tap zoom, swipe between photos and
 * swipe-down to close. Desktop is left untouched (hover-magnify / native
 * modal as configured). Full-res master images (capped 3000px, see
 * product-thumbnail.liquid) keep detail crisp when zoomed.
 */
(function () {
  'use strict';

  // Use PhotoSwipe on touch devices and narrow viewports; leave desktop
  // (fine pointer + wide screen) on Dawn's native hover/lightbox behaviour.
  function shouldIntercept() {
    return (
      window.matchMedia('(pointer: coarse)').matches ||
      window.matchMedia('(max-width: 749px)').matches
    );
  }

  function toSlide(toggle) {
    return {
      src: toggle.dataset.pswpSrc,
      msrc: toggle.dataset.pswpMsrc || undefined,
      width: parseInt(toggle.dataset.pswpWidth, 10) || undefined,
      height: parseInt(toggle.dataset.pswpHeight, 10) || undefined,
      alt: toggle.dataset.pswpAlt || '',
    };
  }

  // Live query (DOM order) so variant changes that reorder media stay in sync.
  function imageToggles(gallery) {
    return Array.from(gallery.querySelectorAll('.product__media-toggle[data-pswp-src]'));
  }

  // One-time "pinch to zoom" hint on the first open (per device).
  var HINT_KEY = 'bi_pswp_hint_seen';
  function registerHint(lightbox) {
    lightbox.on('uiRegister', function () {
      lightbox.pswp.ui.registerElement({
        name: 'pinch-hint',
        order: 9,
        isButton: false,
        appendTo: 'wrapper',
        html: 'Pinch or double-tap to zoom',
        onInit: function (el) {
          el.classList.add('pswp__pinch-hint');
          var seen = false;
          try {
            seen = window.localStorage.getItem(HINT_KEY) === '1';
          } catch (e) {
            seen = false;
          }
          if (seen) {
            el.style.display = 'none';
            return;
          }
          try {
            window.localStorage.setItem(HINT_KEY, '1');
          } catch (e) {
            /* private mode — show once this session, no persistence */
          }
          window.setTimeout(function () {
            el.classList.add('pswp__pinch-hint--hide');
          }, 2600);
        },
      });
    });
  }

  function setupGallery(gallery) {
    if (gallery.dataset.pswpReady === '1') return;
    if (typeof window.__loadPhotoSwipe !== 'function') return; // inline loader missing
    if (!imageToggles(gallery).length) return;
    gallery.dataset.pswpReady = '1';

    var lightbox = null;
    var loading = null;

    function ensureLightbox() {
      if (lightbox) return Promise.resolve(lightbox);
      if (loading) return loading;
      // window.__loadPhotoSwipe is defined inline in the section (see liquid):
      // it owns the dynamic import() so Shopify's asset minifier can't rewrite
      // it into require(). Returns { Lightbox, Core } (both ESM defaults).
      loading = window
        .__loadPhotoSwipe()
        .then(function (mods) {
          var PhotoSwipeLightbox = mods.Lightbox;
          lightbox = new PhotoSwipeLightbox({
            pswpModule: mods.Core, // PhotoSwipe core class — pswpModule accepts it directly
            // World-class touch UX. 'fade' (not 'zoom') because our slides are a
            // plain dataSource with no linked thumbnail elements — the zoom
            // animation has no bounds to anchor to and behaves erratically.
            showHideAnimationType: 'fade',
            bgOpacity: 1,
            initialZoomLevel: 'fit',
            secondaryZoomLevel: 2.5, // double-tap zoom
            maxZoomLevel: 4,
            wheelToZoom: true, // trackpad / mouse-wheel pinch
            closeOnVerticalDrag: true, // swipe down to dismiss
            pinchToClose: true,
            zoom: true,
            counter: true,
          });
          registerHint(lightbox);
          // Hide the sticky store header + announcement bar while open. On iOS
          // Safari a position:sticky header can paint ABOVE a position:fixed
          // overlay, bleeding the store chrome into the full-screen zoom.
          var hideChrome = function () { document.documentElement.classList.add('bi-pswp-active'); };
          var showChrome = function () { document.documentElement.classList.remove('bi-pswp-active'); };
          lightbox.on('beforeOpen', hideChrome);
          // Remove on both 'close' (fires as closing starts) and 'destroy' (after
          // teardown) so the header can never get stuck hidden.
          lightbox.on('close', showChrome);
          lightbox.on('destroy', showChrome);
          lightbox.init();
          return lightbox;
        })
        .catch(function (err) {
          // If PhotoSwipe fails to load, release the guard so Dawn's native
          // behaviour resumes on the next tap rather than dead-ending.
          gallery.dataset.pswpReady = '';
          loading = null;
          console.error('[product-zoom] PhotoSwipe failed to load:', err);
          throw err;
        });
      return loading;
    }

    gallery.addEventListener(
      'click',
      function (event) {
        if (!shouldIntercept()) return; // desktop: leave native zoom alone

        var item = event.target.closest('.product__media-item');
        if (!item) return; // thumbnails / slider buttons → ignore

        var toggle = item.querySelector('.product__media-toggle[data-pswp-src]');
        if (!toggle) return; // video / 3d model → let Dawn's modal handle it

        // Beat both ModalOpener (native lightbox) and magnify.js (img.onclick).
        event.preventDefault();
        event.stopPropagation();

        var toggles = imageToggles(gallery);
        var index = toggles.indexOf(toggle);
        if (index < 0) index = 0;
        var slides = toggles.map(toSlide);

        ensureLightbox()
          .then(function (lb) {
            lb.loadAndOpen(index, slides);
          })
          .catch(function () {
            /* logged in ensureLightbox; native tap works next time */
          });
      },
      true // capture phase — fires before descendant target/bubble handlers
    );
  }

  function init() {
    document.querySelectorAll('media-gallery[data-pswp-enabled]').forEach(setupGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
